import React, { useState } from 'react';
import HotelDetailsModal from './HotelDetailsModal';
import './HotelResults.css';

/**
 * HotelResults Component
 * 
 * Displays hotel search results in beautiful cards
 * Shows name, rating, price, amenities, distance
 */
const HotelResults = ({ hotels, loading, error, onSelectHotel }) => {
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleViewDetails = (hotel) => {
    setSelectedHotel(hotel);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedHotel(null);
  };

  const handleAddToTrip = (hotel) => {
    onSelectHotel(hotel);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="hotel-results-loading">
        <div className="loading-spinner"></div>
        <p>Searching for hotels...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="hotel-results-error">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <h3>Search Failed</h3>
        <p>{error}</p>
      </div>
    );
  }

  // No results
  if (!hotels || hotels.length === 0) {
    return (
      <div className="hotel-results-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
        <h3>No Hotels Found</h3>
        <p>Try adjusting your search criteria</p>
      </div>
    );
  }

  // Helper to render star rating
  const renderStars = (rating) => {
    if (!rating || rating === 0) return null;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <svg 
          key={i} 
          className={i < rating ? 'star-filled' : 'star-empty'}
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="currentColor"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      );
    }
    return <div className="hotel-stars">{stars}</div>;
  };

  return (
    <div className="hotel-results">
      <div className="results-header">
        <h2>Available Hotels</h2>
        <p className="results-count">{hotels.length} hotels found</p>
      </div>

      <div className="hotel-list">
        {hotels.map((hotel, index) => (
          <div key={hotel.hotelId || index} className="hotel-card">
            <div className="hotel-card-content">
              <div className="hotel-info">
                <div className="hotel-header">
                  <h3 className="hotel-name">{hotel.name || 'Hotel'}</h3>
                  {renderStars(hotel.rating)}
                </div>

                {hotel.address && (
                  <p className="hotel-address">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {hotel.address}
                  </p>
                )}

                {hotel.distance && (
                  <p className="hotel-distance">
                    {hotel.distance.toFixed(1)} km from center
                  </p>
                )}

                {(hotel.breakfastIncluded || hotel.freeCancellation || hotel.roomType) && (
                  <div className="hotel-amenities">
                    {hotel.breakfastIncluded && (
                      <span className="amenity-badge">Breakfast Included</span>
                    )}
                    {hotel.freeCancellation && (
                      <span className="amenity-badge">Free Cancellation</span>
                    )}
                    {hotel.roomType && hotel.roomType !== 'STANDARD' && (
                      <span className="amenity-badge">{hotel.roomType}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="hotel-pricing">
                <div className="price-container">
                  <span className="price-label">From</span>
                  <span className="price-amount">${hotel.pricePerNight || hotel.totalPrice || 0}</span>
                  <span className="price-period">per night</span>
                </div>

                <button
                  className="select-hotel-button"
                  onClick={() => handleViewDetails(hotel)}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hotel Details Modal */}
      {showModal && (
        <HotelDetailsModal
          hotel={selectedHotel}
          onClose={handleCloseModal}
          onAddToTrip={handleAddToTrip}
        />
      )}
    </div>
  );
};

export default HotelResults;